import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  LinkIcon,
  LogOut,
  Mail,
  MessageCircle,
  Settings,
  Upload,
  Users,
} from 'lucide-react';

import { NavLink, useNavigate } from 'react-router';

import { useCurrentUser, useSignOut } from '@chartai/api-client';

import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/shared/stores/sidebarStore';
import { useProfileStore } from '@/shared/stores/useProfileStore';

import { SidebarItem } from './SidebarItem';

interface NavSection {
  label: string | null;
  items: ReadonlyArray<{
    icon: typeof Calendar;
    label: string;
    to: string;
    badge?: number;
  }>;
}

const NAV_SECTIONS: readonly NavSection[] = [
  {
    label: null,
    items: [
      { icon: Calendar, label: 'Today', to: '/dashboard/appointments' },
      { icon: Users, label: 'Patients', to: '/dashboard/patients' },
    ],
  },
  {
    label: null,
    items: [
      { icon: AlertCircle, label: 'Review Queue', to: '/dashboard/needs-review', badge: 4 },
      { icon: FileText, label: 'Templates', to: '/dashboard/templates' },
      { icon: Mail, label: 'Referral Letters', to: '/dashboard/referrals' },
    ],
  },
  {
    label: null,
    items: [
      { icon: Upload, label: 'Exports / Activity', to: '/dashboard/exports' },
      { icon: LinkIcon, label: 'Integrations', to: '/dashboard/integrations' },
    ],
  },
  {
    label: null,
    items: [
      { icon: Settings, label: 'Settings', to: '/dashboard/settings' },
    ],
  },
] as const;

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

export function Sidebar() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const selectedProfile = useProfileStore((s) => s.selectedProfile);
  const clearProfile = useProfileStore((s) => s.clear);
  const { user } = useCurrentUser();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const handleSignOut = () => {
    signOut.mutate(undefined, {
      onSuccess: () => {
        clearProfile();
        void navigate('/auth/signin');
      },
    });
  };

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-all duration-200',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      {/* Spacer for top bar alignment */}
      <div className="py-2" />

      {/* Navigation */}
      <nav aria-label="Dashboard navigation" className="flex flex-1 flex-col overflow-y-auto pt-2">
        <div className="flex-1 space-y-0.5 px-2">
          {NAV_SECTIONS.map((section, sIdx) => (
            <div key={section.label ?? `section-${sIdx}`}>
              {sIdx > 0 && (
                <div className="my-2 mx-2 border-t border-sidebar-muted/20" />
              )}
              {section.items.map((item) => (
                <SidebarItem
                  key={item.to}
                  icon={item.icon}
                  label={item.label}
                  to={item.to}
                  collapsed={collapsed}
                  badge={item.badge}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Messages */}
        <div className="px-2 pb-2">
          <div className="mx-2 mb-2 border-t border-sidebar-muted/20" />
          <SidebarItem
            icon={MessageCircle}
            label="Messages"
            to="/dashboard/messages"
            collapsed={collapsed}
            badge={3}
          />
        </div>

        {/* Profile at bottom — links to profile management */}
        <NavLink
          to="/dashboard/profiles"
          className="block border-t border-sidebar-muted/20 px-2 py-3 transition-colors hover:bg-sidebar-hover"
        >
          <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2.5 px-2')}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
              {getInitials(selectedProfile?.displayName ?? 'U')}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">
                  {selectedProfile?.displayName ?? 'Select Profile'}
                </p>
                <p className="truncate text-xs text-sidebar-muted">Provider</p>
              </div>
            )}
          </div>
        </NavLink>

        {/* Logged-in user info + logout */}
        {user && (
          <div className={cn(
            'border-t border-sidebar-muted/20 px-2 py-2',
            collapsed && 'flex flex-col items-center',
          )}>
            {!collapsed && (
              <p className="truncate px-2 text-xs text-sidebar-muted">
                {user.email}
              </p>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signOut.isPending}
              className={cn(
                'mt-1 flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/70 transition-colors hover:bg-sidebar-hover hover:text-white',
                collapsed && 'justify-center px-2',
              )}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="ml-2">{signOut.isPending ? 'Signing out...' : 'Sign Out'}</span>}
            </button>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggle}
          className="mx-2 mb-3 flex items-center justify-center rounded-md p-2 text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-white"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </nav>
    </aside>
  );
}
